import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { cn } from '@/lib/utils';

interface AutocompleteOption {
  id: string;
  label: string;
  [key: string]: any;
}

interface MaterialAutocompleteProps {
  label: string;
  value: AutocompleteOption | null;
  onChange: (value: AutocompleteOption | null) => void;
  onInputChange?: (inputValue: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  fullWidth?: boolean;
  freeSolo?: boolean;
  disableClearable?: boolean;
}

export const MaterialAutocomplete: React.FC<MaterialAutocompleteProps> = ({
  label,
  value,
  onChange,
  onInputChange,
  options,
  placeholder,
  className,
  disabled = false,
  loading = false,
  noOptionsText = "No options found",
  fullWidth = true,
  freeSolo = false,
  disableClearable = false,
  ...props
}) => {
  const handleChange = (event: any, newValue: string | AutocompleteOption | null) => {
    // Only pass object values, ignore string values from freeSolo
    if (typeof newValue === 'object') {
      onChange(newValue);
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    if (onInputChange) {
      onInputChange(newInputValue);
    }
  };

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      disabled={disabled}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      className={cn(className)}
      renderOption={(props, option) => (
        <li
          {...props}
          key={typeof option === 'string' ? option : option.id}
        >
          {typeof option === 'string' ? option : option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              color: 'rgb(17, 24, 39)',
              '& fieldset': {
                borderColor: 'rgb(229, 231, 235)',
              },
              '&:hover fieldset': {
                borderColor: 'rgb(209, 213, 219)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgb(59, 130, 246)',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgb(75, 85, 99)',
              '&.Mui-focused': {
                color: 'rgb(59, 130, 246)',
              },
            },
            '& .MuiInputBase-input': {
              color: 'rgb(17, 24, 39)',
              '&::placeholder': {
                color: 'rgb(107, 114, 128)',
                opacity: 1,
              },
            },
            '& .MuiAutocomplete-endAdornment': {
              '& .MuiSvgIcon-root': {
                color: 'rgb(107, 114, 128)',
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
          }}
        />
      )}
      slotProps={{
        paper: {
          style: {
            backgroundColor: 'white',
            color: 'rgb(17, 24, 39)',
          }
        },
        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              enabled: false, // Disable flipping to prevent dropdown from going up
            },
          ],
        }
      }}
      sx={{
        '& .MuiAutocomplete-option': {
          color: 'rgb(17, 24, 39)',
          '&:hover': {
            backgroundColor: 'rgb(243, 244, 246) !important',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(59, 130, 246, 0.12) !important',
          },
        },
        '& .MuiAutocomplete-noOptions': {
          color: 'rgb(107, 114, 128)',
        },
        '& .MuiAutocomplete-loading': {
          color: 'rgb(107, 114, 128)',
        },
      }}
      {...props}
    />
  );
};
